import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import StatusChip from '@sophocles/components/StatusChip';
import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import { formatDate } from '@sophocles/utils/formatUtils';
import { EditCoverageDialog, ConvertCoverageDialog } from '@sophocles/views/billing/dialogs/PrepaidCoverageDialogs';
import { resolveCoverageOwners } from '@sophocles/views/support/components/prepaidCoverageOwners';

const STATUS_CONFIG_BASE = {
  active: { statusKey: 'billing.prepaidCoverage.statusActive', color: 'ok' },
  scheduled: { statusKey: 'billing.prepaidCoverage.statusScheduled', color: 'warning' },
  expired: { statusKey: 'billing.prepaidCoverage.statusExpired', color: 'neutral' },
  converted: { statusKey: 'billing.prepaidCoverage.statusConverted', color: 'attention' },
};

function PrepaidCoverageCard({ coverages, radioId, onUpdate, isStaff }) {
  const { t } = useTranslation();
  const user = useSelector(getAuthUserSelector);

  const coverageList = coverages && coverages.toJS ? coverages.toJS() : coverages || [];

  const [editTarget, setEditTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const SOURCE_TYPE_LABELS = useMemo(
    () => ({
      order_sale_item: t('billing.prepaidCoverage.sourceOrderSaleItem'),
      manual: t('billing.prepaidCoverage.sourceManual'),
      migration: t('billing.prepaidCoverage.sourceMigration'),
    }),
    [t],
  );

  const editCoverages = useMemo(() => (editTarget ? [editTarget] : []), [editTarget]);

  const cancelRadios = useMemo(
    () => (cancelTarget ? [{ radio_id: radioId, coverage_id: cancelTarget.id }] : []),
    [cancelTarget, radioId],
  );

  const isEditable = coverage => ['active', 'scheduled'].includes(coverage.status);

  return (
    <>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {t('billing.prepaidCoverage.sectionTitle')}
      </Typography>
      {!coverageList || coverageList.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('billing.prepaidCoverage.noCoverageRecords')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {coverageList.map(coverage => {
            const configBase = STATUS_CONFIG_BASE[coverage.status];
            const statusConfig = configBase
              ? { label: t(configBase.statusKey), color: configBase.color }
              : { label: coverage.status || t('general.unknown'), color: 'neutral' };
            const sourceLabel =
              SOURCE_TYPE_LABELS[coverage.source_type] || coverage.source_type || t('general.unknown');
            const { ownerName, effectiveOwnerName, ownersDiffer } = resolveCoverageOwners(coverage);

            return (
              <Box
                key={coverage.id}
                sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <StatusChip label={statusConfig.label} color={statusConfig.color} />
                  {ownerName && (
                    <Typography variant="caption" color="text.secondary">
                      {t('billing.prepaidCoverage.ownerLabel')} {ownerName}
                    </Typography>
                  )}
                  {ownersDiffer && (
                    <Typography variant="caption" color="text.secondary">
                      {t('billing.prepaidCoverage.effectiveOwnerLabel')} {effectiveOwnerName}
                    </Typography>
                  )}
                  <Tooltip title={sourceLabel} sx={{ ml: 'auto !important' }}>
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </Tooltip>
                  {isEditable(coverage) && (
                    <>
                      <Button
                        size="small"
                        sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}
                        onClick={() => setEditTarget(coverage)}
                      >
                        {t('actions.edit')}
                      </Button>
                      <Button
                        size="small"
                        color="info"
                        sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}
                        onClick={() => setCancelTarget(coverage)}
                      >
                        {t('actions.convert')}
                      </Button>
                    </>
                  )}
                </Stack>
                <Typography variant="body2">
                  {coverage.start_date
                    ? formatDate(coverage.start_date, user)
                    : t('billing.prepaidCoverage.notAvailable')}{' '}
                  &mdash;{' '}
                  {coverage.end_date ? formatDate(coverage.end_date, user) : t('billing.prepaidCoverage.notAvailable')}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}

      <EditCoverageDialog
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        coverages={editCoverages}
        onSuccess={onUpdate}
      />

      <ConvertCoverageDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        radios={cancelRadios}
        onSuccess={onUpdate}
      />
    </>
  );
}

export default PrepaidCoverageCard;
